import React, { useCallback, useContext, useState, useMemo } from "react";
import { useApi, useFilter, useMount, useModal } from "hooks";
import { HeaderA } from "components/headers";
import { searchPreAuthTransaction, updatePreAuthTransactionStatus } from "apis";
import { mapPreAuthTransactionResponse, preAuthTransactionListFilterRequest } from "mappers";
import { preAuthTransactionColumns } from "./pre-auth-transaction-columns";
import { Text, DataTable, ButtonLink, Toast, Pill } from "components/commons";
import { VenueContext } from "contexts";
import { ModuleWrapper } from "components";
import Modal from "components/commons/modal/modal";
import ActionButton from "components/commons/action-button/action-button";
import Title from "components/commons/title/title";
import lang from "translations";
import PreAuthTransactionFilter from "./pre-auth-transaction-filter";
import moment from "moment";
import { pluralize, mapObject, preAuthTransactionStatus } from "services";
import { PreAuthStatus, PillType } from "enums";
import { Path } from "paths";
import preAuthStatus from "enums/pre-auth-status";

const PreAuthTransactionListModule = () => {
  const confirmModal = useModal();
  const { venue } = useContext(VenueContext);

  const defaultFilterState = {
    page: 1,
    pageSize: 20,
    venueId: venue?.venueId,
    dateRange: [moment().startOf("day"), moment().endOf("day")],
  };

  const {
    request: fetchPreAuthTransactions,
    loading,
    result = { data: [], metadata: { total: 0 } },
    mappedData,
    error,
  } = useApi({
    api: searchPreAuthTransaction,
    isArray: true,
    mapper: mapPreAuthTransactionResponse,
    handleOwnError: true,
  });

  const {
    filterState,
    requestState,
    modifyFilters,
    modifyFilter,
    clearFilter,
    isFilterDirty,
  } = useFilter(defaultFilterState, defaultFilterState);

  const fetchList = useCallback(
    (newRequestState) => {
      fetchPreAuthTransactions(
        mapObject(newRequestState, preAuthTransactionListFilterRequest)
      );
    },
    [fetchPreAuthTransactions]
  );

  useMount(() => {
    fetchList(requestState);
  });

  const [hasAppliedFilter, setHasAppliedFilter] = useState(isFilterDirty);

  const applyFilterCb = useCallback(
    async (searchKey) => {
      const { requestState: newRequestState } = modifyFilters({
        page: 1,
        searchKey,
      });
      await fetchList(newRequestState);
      setHasAppliedFilter(true);
    },
    [fetchList, modifyFilters]
  );

  const clearFilterCb = useCallback(() => {
    const { requestState: newRequestState } = clearFilter();
    fetchList(newRequestState);
    setHasAppliedFilter(false);
  }, [clearFilter, fetchList]);

  const transactionsResultContent = useMemo(() => {
    if (hasAppliedFilter) {
      return `${result.metadata.total} ${pluralize(
        result.metadata.total,
        lang.searchResult,
        lang.searchResults
      )}`;
    }
    return null;
  }, [result.metadata.total, hasAppliedFilter]);



  const renderGuestStatus = useCallback((status) => {
    if (status === PreAuthStatus.PENDING) {
      return (
        <Pill type={PillType.Green} size="text-xs">
          {preAuthTransactionStatus(status)}
        </Pill>
      );
    }
    if (status === PreAuthStatus.PENDING_COMPLETE) {
      return (
        <Pill type={PillType.Opaque} size="text-xs">
          {preAuthTransactionStatus(status)}
        </Pill>
      );
    }
    return (
      <Pill type={PillType.Reddish} size="text-xs">
        {preAuthTransactionStatus(PreAuthStatus.COMPLETED)}
      </Pill>
    );
  }, []);


  const renderGuest = useCallback(
    (name, tagUid, guestId) => {
      let guestTagDisplay = "";

      guestTagDisplay = (
        <div className="flex items-center">
          <Text className="mr-sm group-hover:text-black-light" tagUid>
            {tagUid}
          </Text>
        </div>);

      return (
        <ButtonLink
          className="grid grid-cols-1 cursor-pointer group"
          path={Path.GUEST_DETAILS_ID(guestId)}
        >
          {name ? (
            <Text color="text-left text-pelorous group-hover:text-pelorous-dark">{name}</Text>
          ) :
            <Text color="text-left text-pelorous group-hover:text-pelorous-dark">
              {[lang.guest, " ", guestId]}
            </Text>
          }
          <div>{guestTagDisplay}</div>
        </ButtonLink>
      );
    },
    []
  );


  const prepareGuestList = useCallback(() => {
    return mappedData.map((guest) => {
      const {
        id, // FIXED (ensure mapper returns lowercase id)
        type,
        amount,
        paidAmount,
        transactionStatus
      } = guest;

      return {
        id,
        type,
        amount,
        paidAmount,
        transactionStatus: renderGuestStatus(transactionStatus),
        user: renderGuest(guest.name, guest.userTagUid, guest.guestId),
        status: transactionStatus
      };
    });
  }, [mappedData, renderGuestStatus, renderGuest]);

  const guests = useMemo(() => prepareGuestList(), [prepareGuestList]);

  const handleCompleteTransaction = async () => {
    const transactionId = confirmModal?.transactionId;
    if (!transactionId) return;
    try {
      await updatePreAuthTransactionStatus({
        transactionId,
        body: {
          transactionStatus: "PENDING_COMPLETE",
          reason: "Checkout",
          updatedBy: "Dashboard",
        },
      });

      Toast({ content: lang.transactionCompleted, success: true }).open();
      confirmModal.close();
      fetchList(requestState);
    } catch (e) {
      confirmModal.close();
      console.error( e.message);
       Toast({
            content: lang.somethingWentWrong,
            error: true,
            icon: "exclamation-fill",
          }).open();
    }
  };

  return (
    <ModuleWrapper>
      <HeaderA
        className="mb-md"
        title={lang.preAuthTransaction}
        description={lang.createAndManagePreAuthTransactions}
      />

      <PreAuthTransactionFilter
        filterState={filterState}
        modifyFilter={modifyFilter}
        applyFilter={applyFilterCb}
        clearFilter={clearFilterCb}
      />

      <DataTable
        resultContent={transactionsResultContent}
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={modifyFilters}
        fetchList={fetchList}
        total={result.metadata.total}
        loading={loading}
        columns={[
          ...preAuthTransactionColumns,
          {
            key: "action",
            actions: true,
            align: "right",
            actionOptions: [
              {
                text: <Text danger>{lang.completeTransaction}</Text>,
                onClick: ({ id, status }) => {
                  if (status === preAuthStatus.PENDING) {
                    confirmModal.show({ transactionId: id });
                  }
                },
              },
            ],
          },
        ]}
        data={guests}
        error={error}
        minWidth="800px"
        hasAppliedFilter={isFilterDirty && hasAppliedFilter}
      />

      <Modal
        active={confirmModal.active}
        actionContent={
          <ActionButton
            className="px-md py-md"
            secondary={{
              text: lang.cancel,
              onClick: confirmModal.close,
            }}
            primary={{
              text: lang.complete,
              type: "primary",
              onClick: handleCompleteTransaction,
            }}
          />
        }
        title={<Title lg>{lang.completeTransaction}</Title>}
        close={confirmModal.close}
      >
        <Text size="text-md">{lang.confirmCompleteTransaction}</Text>
      </Modal>
    </ModuleWrapper>
  );
};

export default PreAuthTransactionListModule;